"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
type HomeTab = "feed" | "latest";
const HOME_TAB_STORAGE_KEY = "home.activeTab";
export const HOME_TAB_EVENT = "homeTabChange";
const getBrowserWindow = () => {
  const {
    window: browserWindow
  } = globalThis as typeof globalThis & {
    window?: Window | undefined;
  };
  return browserWindow;
};
export const getStoredHomeTab = (): HomeTab => {
  const browserWindow = getBrowserWindow();
  if (browserWindow === undefined) {
    return "latest";
  }
  try {
    const saved = browserWindow.localStorage.getItem(HOME_TAB_STORAGE_KEY);
    return saved === "feed" || saved === "latest" ? saved : "latest";
  } catch (error) {
    console.warn("Failed to read home tab from storage", error);
    return "latest";
  }
};
export const setStoredHomeTab = (tab: HomeTab) => {
  const browserWindow = getBrowserWindow();
  if (browserWindow === undefined) {
    return;
  }
  try {
    const current = browserWindow.localStorage.getItem(HOME_TAB_STORAGE_KEY);
    if (current === tab) {
      browserWindow.dispatchEvent(new CustomEvent(HOME_TAB_EVENT, {
        detail: {
          tab
        }
      }));
      return;
    }
    browserWindow.localStorage.setItem(HOME_TAB_STORAGE_KEY, tab);
    browserWindow.dispatchEvent(new CustomEvent(HOME_TAB_EVENT, {
      detail: {
        tab
      }
    }));
  } catch (error) {
    console.warn("Failed to persist home tab to storage", error);
  }
};
export function useHomeTabs() {
  const $ = _c(6);
  const [activeTab, setActiveTab] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const browserWindow_0 = getBrowserWindow();
      if (browserWindow_0 === undefined) {
        return;
      }
      const handleTabEvent = event => {
        const detail = (event as CustomEvent<{
          tab?: HomeTab | undefined;
        }>).detail;
        if (!detail?.tab) {
          return;
        }
        if (detail.tab !== "feed" && detail.tab !== "latest") {
          return;
        }
        setActiveTab(detail.tab);
      };
      browserWindow_0.addEventListener(HOME_TAB_EVENT, handleTabEvent as EventListener);
      return () => {
        browserWindow_0.removeEventListener(HOME_TAB_EVENT, handleTabEvent as EventListener);
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = tab => {
      setActiveTab(tab);
      setStoredHomeTab(tab);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleTabChange = t3;
  let t4;
  if ($[4] !== activeTab) {
    t4 = {
      activeTab,
      handleTabChange
    };
    $[4] = activeTab;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4 as const;
}
function _temp2() {
  const browserWindow = getBrowserWindow();
  if (browserWindow === undefined) {
    return;
  }
  const params = new URLSearchParams(browserWindow.location.search);
  if (!params.has("tab")) {
    return;
  }
  params.delete("tab");
  const search = params.toString();
  const queryPrefix = search ? `?${search}` : "";
  const hash = browserWindow.location.hash ?? "";
  const nextUrl = `${browserWindow.location.pathname}${queryPrefix}${hash}`;
  browserWindow.history.replaceState(null, "", nextUrl);
}
function _temp() {
  return getStoredHomeTab();
}
export type { HomeTab };